import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import SelectField from "components/admin/SelectField";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import RecipientPicker from "layouts/mail/RecipientPicker";
import TestPackCard from "layouts/mail/TestPackCard";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { addDays, formatDate, formatNumber } from "utils/format";

/*
 * Bố cục (người dùng: CSKH / vận hành gửi quà đền bù, quà sự kiện):
 *   [Gửi thư]: 1 Gửi cho ai → 2 Nội dung → 3 Quà kèm → 4 Hạn & loại thư → Tóm tắt trước khi gửi → nút gửi
 *              (nút mờ có dòng nói vì sao). Gửi toàn server: hộp xác nhận ghi số nhân vật + quà, phải gõ "gửi toàn server".
 *   [Thư toàn server đang hiệu lực]: thu hồi.
 *   [Công cụ test] gập sẵn ở cuối.
 */

const MAIL_TYPES = [
  { value: 1, label: "Admin", help: "Thư thường từ ban quản trị" },
  { value: 2, label: "Sự kiện", help: "Quà / thông báo sự kiện" },
  { value: 3, label: "Hoàn trả / đền bù", help: "Đền bù lỗi, bảo trì, trả lại đồ" },
  { value: 4, label: "Hệ thống", help: "Thông báo hệ thống" },
];

const EXPIRE_PRESETS = [1, 3, 7, 14, 30, 60, 90];

/** Theme gốc tô nút đang chọn rất nhạt (nền tối gần như không đọc được): tô màu rõ cho cả hai nền. */
const TOGGLE_SX = ({ palette }) => ({
  "& .MuiToggleButton-root": {
    color: palette.text.main,
    borderColor: palette.grey[400],
    px: 1.5,
  },
  "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
    color: palette.white.main,
    backgroundColor: palette.info.main,
  },
});
const CONFIRM_PHRASE = "gửi toàn server";

/** Bỏ dấu, chữ thường, gộp khoảng trắng: gõ "gui toan server" hay "Gửi toàn server" đều được. */
const normalize = (text) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const expireText = (days) =>
  Number(days) > 0
    ? `sau ${days} ngày (tới ngày ${addDays(Number(days)).toLocaleDateString("vi-VN")})`
    : "không hết hạn";

function Step({ number, title, hint, children }) {
  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
        <MDBox
          width={24}
          height={24}
          borderRadius="50%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgColor="info"
          color="white"
          sx={{ fontSize: 13, fontWeight: "bold", flexShrink: 0 }}
        >
          {number}
        </MDBox>
        <MDTypography variant="button" fontWeight="bold">
          {title}
        </MDTypography>
        {hint && (
          <MDTypography variant="caption" color="text">
            {hint}
          </MDTypography>
        )}
      </MDBox>
      <MDBox pl={{ xs: 0, sm: 4 }} display="flex" flexDirection="column" gap={1.5}>
        {children}
      </MDBox>
    </MDBox>
  );
}

Step.defaultProps = { hint: null };
Step.propTypes = {
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  children: PropTypes.node.isRequired,
};

function SummaryRow({ label, children }) {
  return (
    <MDBox
      display="flex"
      gap={{ xs: 0, sm: 2 }}
      py={0.5}
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems={{ sm: "flex-start" }}
    >
      <MDTypography
        variant="button"
        fontWeight="bold"
        color="text"
        display="block"
        lineHeight={1.6}
        sx={{ width: { sm: 110 }, flexShrink: 0 }}
      >
        {label}
      </MDTypography>
      <MDBox flex={1} minWidth={0} lineHeight={1.6}>
        {typeof children === "string" ? (
          <MDTypography variant="button" display="block" lineHeight={1.6}>
            {children}
          </MDTypography>
        ) : (
          children
        )}
      </MDBox>
    </MDBox>
  );
}

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/** Hộp xác nhận gửi toàn server: ghi rõ số người + quà, phải gõ lại cụm xác nhận. */
function SendAllDialog({ form, gift, playerCount, busy, onClose, onSubmit }) {
  const [typed, setTyped] = useState("");
  const ok = normalize(typed) === normalize(CONFIRM_PHRASE);
  return (
    <ActionDialog
      open
      title="Gửi thư cho TOÀN SERVER"
      color="error"
      busy={busy}
      disabled={!ok}
      submitText="Gửi toàn server"
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <Alert severity="warning">
        Thư sẽ tới{" "}
        <b>
          {playerCount != null ? `toàn bộ ${formatNumber(playerCount)} nhân vật` : "mọi nhân vật"}
        </b>
        {form.includeNewPlayers ? ", cả nhân vật tạo sau khi gửi" : ""}. Đã gửi thì người chơi nhận
        được quà ngay; thu hồi chỉ chặn được người chưa nhận.
      </Alert>
      <MDBox>
        <SummaryRow label="Tiêu đề">{form.title}</SummaryRow>
        <SummaryRow label="Quà">{gift ? <GiftPreview gift={gift} /> : "Không kèm quà"}</SummaryRow>
        <SummaryRow label="Hết hạn">{expireText(form.expireDays)}</SummaryRow>
      </MDBox>
      <MDInput
        fullWidth
        autoFocus
        label={`Gõ "${CONFIRM_PHRASE}" để xác nhận`}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
      />
    </ActionDialog>
  );
}

SendAllDialog.defaultProps = { gift: null, playerCount: null };
SendAllDialog.propTypes = {
  form: PropTypes.shape({
    title: PropTypes.string,
    expireDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    includeNewPlayers: PropTypes.bool,
  }).isRequired,
  gift: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  playerCount: PropTypes.number,
  busy: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

function Mail() {
  const [params] = useSearchParams();
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const [target, setTarget] = useState("one");
  const [recipients, setRecipients] = useState([]);
  const [withGift, setWithGift] = useState(false);
  const [customExpire, setCustomExpire] = useState(false);
  const [sendAllOpen, setSendAllOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [form, setForm] = useState({
    type: 1,
    title: "",
    content: "",
    gift: [],
    expireDays: 30,
    includeNewPlayers: false,
  });
  const globals = useFetch(() => api.get("/api/admin/mail/global", { limit: 100 }), []);
  const canSend = useCan(PERMISSIONS.MAIL_SEND);
  const playerCount = useFetch(
    () => (target === "all" ? api.get("/api/admin/mail/player-count") : Promise.resolve(null)),
    [target]
  );

  useEffect(() => {
    const userId = Number(params.get("userId")) || null;
    const name = params.get("name");
    if (!userId && !name) return;
    api
      .get("/api/admin/mail/recipients", { q: userId ? String(userId) : name, size: 20 })
      .then((res) => {
        const match = res.find((r) => (userId ? r.userId === userId : r.name === name));
        if (match?.hasPlayer) setRecipients([match]);
      })
      .catch(() => {});
  }, []);

  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });
  const clearAfterSend = () => setForm({ ...form, title: "", content: "", gift: [] });

  const gift = withGift && form.gift.length ? form.gift : null;
  const isAll = target === "all";
  const count = typeof playerCount.data === "number" ? playerCount.data : null;

  // Lý do chưa gửi được, hiện ngay trên nút gửi.
  const blockers = [
    !isAll && recipients.length === 0 && "Chưa chọn người nhận (bước 1).",
    !form.title.trim() && "Chưa có tiêu đề thư (bước 2).",
    withGift &&
      form.gift.length === 0 &&
      "Đã bật kèm quà nhưng chưa thêm món nào (bước 3) — thêm quà hoặc tắt kèm quà.",
    !(Number(form.expireDays) >= 0) && "Số ngày hết hạn không hợp lệ (bước 4).",
  ].filter(Boolean);

  const body = () => ({
    type: Number(form.type),
    title: form.title,
    content: form.content,
    expireDays: Number(form.expireDays),
    includeNewPlayers: form.includeNewPlayers,
    gift,
  });

  const sendAll = async () => {
    const res = await run(
      () => api.post("/api/admin/mail/send-all", body()),
      (data) => `Đã gửi thư "${form.title}" tới ${formatNumber(data.count)} nhân vật`
    );
    if (res.ok) {
      setSendAllOpen(false);
      clearAfterSend();
      globals.reload(true);
    }
  };

  const send = async () => {
    if (blockers.length) return;
    if (isAll) {
      setSendAllOpen(true);
      return;
    }
    if (recipients.length > 1) {
      const ok = await confirm({
        title: `Gửi thư cho ${recipients.length} người`,
        message: `Gửi "${form.title}" ${gift ? "kèm quà " : ""}tới: ${recipients
          .map((r) => r.name || r.username)
          .join(", ")}?`,
        confirmText: `Gửi cho ${recipients.length} người`,
      });
      if (!ok) return;
    }
    const res = await run(
      () =>
        api.post("/api/admin/mail/send", { ...body(), userIds: recipients.map((r) => r.userId) }),
      (data) =>
        `Đã gửi ${formatNumber(data.count)} thư${
          data.skipped?.length ? `, bỏ qua ${data.skipped.length} tài khoản chưa có nhân vật` : ""
        }`
    );
    if (res.ok) clearAfterSend();
  };

  const revoke = async (mail) => {
    const ok = await confirm({
      title: "Thu hồi thư toàn server",
      message: `Thu hồi thư "${
        mail.title
      }"? Người chưa nhận sẽ không thấy thư nữa; ai đã nhận quà (${formatNumber(
        mail.claimed || 0
      )} người) thì vẫn giữ quà.`,
      color: "error",
      confirmText: "Thu hồi",
    });
    if (!ok) return;
    const res = await run(() => api.post(`/api/admin/mail/global/${mail.id}/revoke`), "Đã thu hồi");
    if (res.ok) globals.reload(true);
  };

  const sendLabel = isAll
    ? `Gửi toàn server${count != null ? ` (${formatNumber(count)} nhân vật)` : ""}…`
    : `Gửi cho ${recipients.length || 0} người`;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="mail" />
        <Grid container spacing={3}>
          {canSend && (
            <Grid item xs={12} lg={7}>
              <PageCard
                title="Gửi thư"
                subtitle="Người chơi mở hòm thư trong game để đọc và nhận quà"
              >
                <MDBox
                  component="form"
                  display="flex"
                  flexDirection="column"
                  gap={3}
                  onSubmit={(event) => {
                    event.preventDefault();
                    send();
                  }}
                >
                  <Step number={1} title="Gửi cho ai">
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={target}
                      onChange={(e, value) => value && setTarget(value)}
                      sx={TOGGLE_SX}
                    >
                      <ToggleButton value="one">
                        <Icon fontSize="small">person</Icon>&nbsp;Một vài người
                      </ToggleButton>
                      <ToggleButton value="all">
                        <Icon fontSize="small">public</Icon>&nbsp;Toàn server
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {isAll ? (
                      <>
                        <Alert severity="warning">
                          Thư sẽ tới{" "}
                          <b>
                            {count != null
                              ? `toàn bộ ${formatNumber(count)} nhân vật`
                              : "mọi nhân vật"}
                          </b>
                          . Kiểm tra kỹ quà trước khi gửi.
                        </Alert>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.includeNewPlayers}
                              onChange={(e) =>
                                setForm({ ...form, includeNewPlayers: e.target.checked })
                              }
                            />
                          }
                          label="Người tạo nhân vật sau khi gửi cũng nhận được (tới khi thư hết hạn)"
                        />
                      </>
                    ) : (
                      <RecipientPicker value={recipients} onChange={setRecipients} />
                    )}
                  </Step>

                  <Step number={2} title="Nội dung thư">
                    <MDInput
                      label="Tiêu đề"
                      value={form.title}
                      onChange={set("title")}
                      required
                      placeholder="Ví dụ: Đền bù bảo trì ngày 2/11"
                    />
                    <MDInput
                      label="Lời nhắn (không bắt buộc)"
                      value={form.content}
                      onChange={set("content")}
                      multiline
                      minRows={3}
                    />
                  </Step>

                  <Step number={3} title="Quà kèm theo">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={withGift}
                          onChange={(e) => setWithGift(e.target.checked)}
                        />
                      }
                      label={withGift ? "Có kèm quà" : "Không kèm quà — chỉ gửi lời nhắn"}
                    />
                    {withGift && (
                      <GiftEditor
                        value={form.gift}
                        onChange={(next) => setForm((prev) => ({ ...prev, gift: next }))}
                      />
                    )}
                  </Step>

                  <Step number={4} title="Hạn thư và loại thư">
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <SelectField
                          fullWidth
                          label="Thư tự biến mất"
                          value={customExpire ? "custom" : form.expireDays}
                          onChange={(e) => {
                            if (e.target.value === "custom") {
                              setCustomExpire(true);
                              return;
                            }
                            setCustomExpire(false);
                            setForm({ ...form, expireDays: e.target.value });
                          }}
                        >
                          {EXPIRE_PRESETS.map((d) => (
                            <MenuItem key={d} value={d}>
                              Sau {d} ngày
                            </MenuItem>
                          ))}
                          <MenuItem value={0}>Không bao giờ</MenuItem>
                          <MenuItem value="custom">Số ngày khác…</MenuItem>
                        </SelectField>
                      </Grid>
                      {customExpire && (
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            fullWidth
                            type="number"
                            label="Số ngày"
                            value={form.expireDays}
                            inputProps={{ min: 0 }}
                            onChange={set("expireDays")}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <SelectField
                          fullWidth
                          label="Loại thư"
                          value={form.type}
                          onChange={set("type")}
                        >
                          {MAIL_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </SelectField>
                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                          {MAIL_TYPES.find((t) => t.value === Number(form.type))?.help}
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </Step>

                  <MDBox
                    p={2}
                    borderRadius="lg"
                    sx={{
                      border: 2,
                      borderColor: isAll ? "warning.main" : "info.main",
                    }}
                  >
                    <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                      Kiểm tra lại trước khi gửi
                    </MDTypography>
                    <SummaryRow label="Gửi tới">
                      {isAll ? (
                        <MDTypography
                          variant="button"
                          fontWeight="bold"
                          color="warning"
                          display="block"
                          lineHeight={1.6}
                        >
                          TOÀN SERVER
                          {count != null ? ` — ${formatNumber(count)} nhân vật` : ""}
                          {form.includeNewPlayers ? " + người tạo sau" : ""}
                        </MDTypography>
                      ) : (
                        <MDTypography variant="button" display="block" lineHeight={1.6}>
                          {recipients.length === 0
                            ? "Chưa chọn"
                            : recipients.map((r) => r.name || r.username).join(", ")}
                        </MDTypography>
                      )}
                    </SummaryRow>
                    <SummaryRow label="Tiêu đề">{form.title.trim() || "Chưa có"}</SummaryRow>
                    <SummaryRow label="Quà">
                      {gift ? <GiftPreview gift={gift} /> : "Không kèm quà"}
                    </SummaryRow>
                    <SummaryRow label="Hết hạn">{expireText(form.expireDays)}</SummaryRow>
                  </MDBox>

                  <MDBox>
                    {blockers.map((b) => (
                      <MDTypography key={b} variant="caption" color="text" display="block" mb={0.5}>
                        <Icon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.5 }}>
                          info
                        </Icon>
                        {b}
                      </MDTypography>
                    ))}
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color={isAll ? "warning" : "info"}
                      disabled={busy || blockers.length > 0}
                      sx={{ mt: 1 }}
                    >
                      <Icon>send</Icon>&nbsp;{sendLabel}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </PageCard>
            </Grid>
          )}
          <Grid item xs={12} lg={canSend ? 5 : 12}>
            <PageCard
              title="Thư toàn server đang hiệu lực"
              subtitle="Người chơi vẫn còn nhận được"
              color="warning"
              noPadding
              actions={
                <MDButton
                  size="small"
                  variant="outlined"
                  color="white"
                  onClick={() => globals.reload()}
                >
                  <Icon>refresh</Icon>&nbsp;Tải lại
                </MDButton>
              }
            >
              {globals.error && (
                <MDBox px={2}>
                  <Alert severity="error">Không tải được danh sách: {globals.error}</Alert>
                </MDBox>
              )}
              <AdminTable
                loading={globals.loading}
                rows={globals.data || []}
                emptyText="Không có thư toàn server nào đang hiệu lực"
                columns={[
                  {
                    key: "title",
                    label: "Thư",
                    wrap: true,
                    render: (m) => (
                      <MDBox>
                        <Tooltip title={`mã thư ${m.id}`}>
                          <span>{m.title}</span>
                        </Tooltip>
                        <MDBox display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                          {m.hasGift ? (
                            <Chip
                              size="small"
                              color="success"
                              label={`${formatNumber(m.claimed)} người đã nhận quà`}
                            />
                          ) : (
                            <Chip size="small" label="Chỉ lời nhắn" />
                          )}
                          {m.includeNew && <Chip size="small" label="Cả người tạo sau" />}
                        </MDBox>
                      </MDBox>
                    ),
                  },
                  {
                    key: "time",
                    label: "Gửi lúc / hết hạn",
                    render: (m) => (
                      <MDBox>
                        {formatDate(m.createTime)}
                        <br />
                        {m.expireTime ? formatDate(m.expireTime) : "Không hết hạn"}
                      </MDBox>
                    ),
                  },
                  canSend && {
                    key: "actions",
                    label: "",
                    align: "right",
                    render: (m) => (
                      <MDButton
                        size="small"
                        variant="text"
                        color="error"
                        disabled={busy}
                        onClick={() => revoke(m)}
                      >
                        Thu hồi
                      </MDButton>
                    ),
                  },
                ].filter(Boolean)}
              />
            </PageCard>
          </Grid>
        </Grid>

        {canSend && (
          <Card sx={{ mt: 3 }}>
            <MDBox
              component="button"
              type="button"
              onClick={() => setTestOpen((v) => !v)}
              aria-expanded={testOpen}
              px={2}
              py={1.5}
              display="flex"
              alignItems="center"
              gap={1}
              width="100%"
              sx={{
                cursor: "pointer",
                border: 0,
                background: "transparent",
                textAlign: "left",
                color: "inherit",
              }}
            >
              <Icon color="error">science</Icon>
              <MDBox flex={1}>
                <MDTypography variant="button" fontWeight="bold" display="block">
                  Công cụ test — chỉ dùng trên máy chủ test
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Tặng toàn bộ vật phẩm và pet để thử game. Đừng dùng trên máy chủ thật.
                </MDTypography>
              </MDBox>
              <Icon>{testOpen ? "expand_less" : "expand_more"}</Icon>
            </MDBox>
            <Collapse in={testOpen} unmountOnExit>
              <MDBox px={2} pb={2}>
                <TestPackCard onSent={() => globals.reload(true)} />
              </MDBox>
            </Collapse>
          </Card>
        )}
      </MDBox>
      <Footer />

      {sendAllOpen && (
        <SendAllDialog
          form={form}
          gift={gift}
          playerCount={count}
          busy={busy}
          onClose={() => setSendAllOpen(false)}
          onSubmit={sendAll}
        />
      )}
    </DashboardLayout>
  );
}

export default Mail;
